import type { ReactNode } from 'react';

export interface FormFieldProps {
    /** Label text — 13px/500, `--color-text-secondary` (COMPONENTS.md → Label). */
    label: string;
    required?: boolean;
    htmlFor?: string;
    /** 12px muted helper line. Replaced by `error` when present (Field spec). */
    helper?: ReactNode;
    /** 12px `--color-error-fg` message; when set, the control also gets the danger ring. */
    error?: string | null;
    /** Spans both grid columns of the parent `.portal-field-grid`. */
    fullWidth?: boolean;
    children: ReactNode;
}

/**
 * EAA-1822: the label + control + helper/error unit — COMPONENTS.md's "Field"
 * component (`display:flex; flex-direction:column; gap:6px`). Frames 19/20 use
 * this shape for every input, select and the two-column form grid.
 *
 * Validation is inline and on blur (never on keystroke) per the ticket's
 * behaviour rules — this component only renders whatever `error` the caller
 * computed; it does not itself decide touched/blur timing (that lives in the
 * page's field-state hook, shared across edit + onboarding).
 */
export function FormField({
    label,
    required,
    htmlFor,
    helper,
    error,
    fullWidth,
    children,
}: FormFieldProps) {
    return (
        <label
            className={'portal-field' + (fullWidth ? ' portal-field-span-2' : '')}
            htmlFor={htmlFor}
        >
            <span className="portal-field-label">
                {label}
                {required && (
                    <span className="portal-field-required" aria-hidden="true">
                        {' '}
                        *
                    </span>
                )}
            </span>
            {children}
            {error ? (
                <span className="portal-field-error" role="alert">
                    {error}
                </span>
            ) : (
                helper && <span className="portal-field-helper">{helper}</span>
            )}
        </label>
    );
}

export default FormField;
